import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import {
    ExTable,
    SelectionFixedFooter,
    TableBody,
    TableCell,
    TableHead,
    TableWrapper,
    useMultipleCheckForm,
} from 'components/shared/widget'
import { useFetchProductItemsApi } from 'api/product_item'
import { ProductItem, ProductItemSearchForm } from 'entities/product_item'
import { Pagination } from 'components/shared/pagination'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { SearchConditionView } from 'components/shared/search_drawer'
import { GlobalStateContext } from 'contexts/global_state_context'
import { productItemColumnInfoList, ProductItemTableData } from './product_item_table'
import { TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import qs from 'qs'
import { useState } from 'react'
import { Flex } from 'components/shared/flex'
import { Button } from 'react-bootstrap'
import { EditProductItemForm, NewProductItemForm } from './product_item_form'
import { ProtocolVersion } from 'entities/protocol_version'
import { ProtocolVersionProbesModal } from 'components/protocol_version/protocol_version_probes_modal'

type Props = {
    history: H.History
}

const ProductItemsPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const query = useQuery<ProductItemSearchForm>()
    const searchForm = useForm<ProductItemSearchForm>({ ...query, ordering: 'product_item_id' })
    const api = useFetchProductItemsApi(searchForm)
    const checkIdForm = useMultipleCheckForm<ProductItem>()
    const productItemDisplaySetting = useDisplaySetting(productItemColumnInfoList(searchForm), 'ProductItem')
    const [selectedProductItem, setSelectedProductItem] = useState<ProductItem>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const [showProbe, setShowProbe] = useState<boolean>(false)
    const [selectedProtocolVersion, setSelectedProtocolVersion] = useState<ProtocolVersion>({})

    useEffect(() => {
        api.execute()
    }, [])

    useEffectSkipFirst(() => {
        searchForm.set(query)
    }, [query.scope])

    const handleClickRemoveFilter = (key: keyof ProductItemSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    useEffect(() => {
        const queryParams = qs.stringify({ ...searchForm.object, page: api.pageSet.page, perPage: api.pageSet.perPage })
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`product_items?${queryParams}`)
        }
    }, [searchForm.object])

    return (
        <div key={query.scope}>
            <EditProductItemForm
                productItem={selectedProductItem}
                show={showEdit}
                onClose={() => setShowEdit(false)}
                onComplete={() => {
                    api.execute()
                    setShowEdit(false)
                }}
            />
            <ProtocolVersionProbesModal show={showProbe} protocolVersion={selectedProtocolVersion} onClose={() => setShowProbe(false)} />
            <ContentHeader
                headerTitle={query.scope === 'only_qc' ? '標準尿QC' : '製品'}
                left={
                    <>
                        {query.scope !== 'only_qc' && <NewProductItemForm onComplete={() => api.execute()} />}
                        <SearchConditionView
                            searchForm={searchForm}
                            onClickRemoveButton={handleClickRemoveFilter}
                            hiddenSearchParams={['institutionId', 'scope']}
                        />
                    </>
                }
                right={<>{/* <DisplaySettingModal displaySettings={displaySettings} /> */}</>}
            />
            <Flex style={{ marginTop: 5 }} alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                    {/* <InputField
                        attr="name"
                        form={preSearchForm}
                        placeholder="名前で検索"
                        onPressEnter={() => {
                            searchForm.updateObject('name', preSearchForm.getValue('name'))
                        }}
                        style={{ marginBottom: 0, width: 250 }}
                    /> */}
                </Flex>
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper showFooter={checkIdForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <tr>
                                <TableHeads displaySetting={productItemDisplaySetting} />

                                <TableCell th width={140}></TableCell>
                                <TableCell th width={140}></TableCell>
                            </tr>
                        </TableHead>
                        <TableBody loading={api.loading} columnLength={productItemDisplaySetting.getColumnLength() + 2}>
                            {api.response.results.map((productItem: ProductItem) => {
                                return (
                                    <tr key={`productItem-${productItem.id}`}>
                                        <ProductItemTableData
                                            onClickProbe={() => {
                                                setSelectedProtocolVersion(productItem.currentProtocolVersion!)
                                                setShowProbe(true)
                                            }}
                                            onClickQcProbe={() => {
                                                setSelectedProtocolVersion(productItem.qcVersion!)
                                                setShowProbe(true)
                                            }}
                                            productItem={productItem}
                                            displaySetting={productItemDisplaySetting}
                                        />

                                        <TableCell align="center">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => {
                                                    props.history.push(
                                                        `protocol_versions?productItemId=${productItem.id}${
                                                            productItem.isStandardQc || productItem.isRnaStandardQc ? '&isStandardQc=true' : ''
                                                        }`
                                                    )
                                                }}
                                            >
                                                プロトコル確認
                                            </Button>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => {
                                                    setSelectedProductItem(productItem)
                                                    setShowEdit(true)
                                                }}
                                            >
                                                バージョン変更
                                            </Button>
                                        </TableCell>
                                    </tr>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                </TableWrapper>
            </ContentBody>

            <SelectionFixedFooter
                ids={checkIdForm.ids()}
                onClickReset={() => checkIdForm.form.resetForm()}
                searchForm={searchForm}
                searchAttr="id"
                tableName="ProductItem"
            ></SelectionFixedFooter>
        </div>
    )
}

export default withRouter(ProductItemsPage)
